import { useRef } from 'react';

export interface ReCaptchaFunctions {
  execute: (action: string) => Promise<string>;
  loadScript: () => void;
}

declare global {
  interface Window {
    grecaptcha?: {
      enterprise: {
        ready: (callback: () => void) => void;
        execute: (
          siteKey: string,
          options: { action: string }
        ) => Promise<string>;
      };
    };
  }
}

export const useReCaptcha = (): ReCaptchaFunctions => {
  const waitPromiseRef = useRef<Promise<any> | null>(null);
  const scriptLoadedRef = useRef<boolean>(false);
  const timeoutMs = 5000;

  const loadScript = (): void => {
    if (scriptLoadedRef.current) {
      return;
    }

    const existingScript = document.querySelector(
      'script[src*="recaptcha/enterprise.js"]'
    );
    if (existingScript) {
      scriptLoadedRef.current = true;
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${process.env.REACT_APP_RECAPTCHA_SITE_KEY}`;
    script.async = true;
    document.head.appendChild(script);
    scriptLoadedRef.current = true;
  };

  const execute = async (action: string): Promise<string> => {
    const grecaptcha = await waitForRecaptcha();

    return new Promise((resolve, reject) => {
      grecaptcha.enterprise.ready(async () => {
        try {
          const token = await grecaptcha.enterprise.execute(
            process.env.REACT_APP_RECAPTCHA_SITE_KEY as string,
            { action }
          );
          resolve(token);
        } catch (e) {
          reject(e);
        }
      });
    });
  };

  const waitForRecaptcha = (): Promise<any> => {
    if (waitPromiseRef.current) {
      return waitPromiseRef.current;
    }

    waitPromiseRef.current = new Promise((resolve, reject) => {
      const start = Date.now();

      const check = () => {
        const grecaptcha = window.grecaptcha;

        if (grecaptcha && grecaptcha.enterprise) {
          return resolve(grecaptcha);
        }

        if (Date.now() - start >= timeoutMs) {
          return reject(new Error('reCAPTCHA failed to load'));
        }

        setTimeout(check, 100);
      };

      check();
    });

    return waitPromiseRef.current;
  };

  return { execute, loadScript };
};
